import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPatientsCount, getPatients } from '../../services/patientService';
import { getCasesCount } from '../../services/caseService';
import { getUserAuditLogs } from '../../services/auditService';
import { hasPermission } from '../../constants/userRoles';

const Dashboard = () => {
  const { userProfile } = useAuth();
  const [stats, setStats] = useState({
    patients: 0,
    cases: 0,
    photos: 0,
    activeCases: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [userProfile]);

  const loadDashboardData = async () => {
    if (!userProfile) return;

    try {
      setLoading(true);
      const [patientsCount, casesCount] = await Promise.all([
        getPatientsCount(userProfile.practiceId),
        getCasesCount(userProfile.practiceId)
      ]);

      setStats({
        patients: patientsCount,
        cases: casesCount,
        photos: 0,
        activeCases: casesCount
      });

      // Load recent patients
      try {
        const patients = await getPatients(userProfile.practiceId, userProfile.id);
        setRecentPatients(patients.slice(0, 5));
      } catch (e) {
        // Non-critical
      }

      // Load recent audit logs
      if (hasPermission(userProfile.role, 'viewAuditLogs')) {
        try {
          const logs = await getUserAuditLogs(userProfile.practiceId, 10);
          setRecentActivity(logs);
        } catch (e) {
          // Non-critical
        }
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  const formatTime = (date) => {
    if (!date) return '';
    if (date.toDate) return date.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getActivityDescription = (log) => {
    const actions = {
      create: 'Created',
      read: 'Viewed',
      update: 'Updated',
      delete: 'Deleted',
      upload: 'Uploaded',
      download: 'Downloaded',
      login: 'Logged in',
      logout: 'Logged out',
      access_denied: 'Access denied'
    };
    const action = actions[log.action] || log.action;
    const resource = log.resourceType || '';
    return `${action} ${resource}`;
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Welcome back, {userProfile?.displayName}!
        </h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
          Here's an overview of your dental practice
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 rounded-md p-2.5 sm:p-3">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Patients</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats.patients}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 rounded-md p-2.5 sm:p-3">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Cases</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats.cases}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-100 rounded-md p-2.5 sm:p-3">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Photos</p>
              <p className="text-xl sm:text-2xl font-semibold text-gray-900">
                {loading ? '...' : stats.photos}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-amber-100 rounded-md p-2.5 sm:p-3">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">HIPAA</p>
              <p className="text-base sm:text-lg font-semibold text-green-600">Compliant</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Quick Actions */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg shadow-lg p-4 sm:p-6 text-white lg:col-span-2">
          <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <Link
              to="/patients/new"
              className="bg-white bg-opacity-15 hover:bg-opacity-25 rounded-lg p-4 text-center transition"
            >
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              <p className="text-sm font-medium">New Patient</p>
            </Link>
            <Link
              to="/patients"
              className="bg-white bg-opacity-15 hover:bg-opacity-25 rounded-lg p-4 text-center transition"
            >
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sm font-medium">View Patients</p>
            </Link>
            <Link
              to="/photos"
              className="bg-white bg-opacity-15 hover:bg-opacity-25 rounded-lg p-4 text-center transition"
            >
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm font-medium">Photo Gallery</p>
            </Link>
            <Link
              to="/cases"
              className="bg-white bg-opacity-15 hover:bg-opacity-25 rounded-lg p-4 text-center transition"
            >
              <svg className="h-8 w-8 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm font-medium">View Cases</p>
            </Link>
          </div>
        </div>

        {/* Treatment Workflow */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Treatment Workflow</h3>
          <div className="space-y-3">
            {[
              { phase: 'Initial Records', color: 'blue', step: 1 },
              { phase: 'Post-Ortho', color: 'green', step: 2 },
              { phase: 'Provisional', color: 'amber', step: 3 },
              { phase: 'Final Records', color: 'purple', step: 4 },
            ].map((item) => (
              <div key={item.phase} className="flex items-center">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-${item.color}-100 flex items-center justify-center`}>
                  <span className={`text-xs font-bold text-${item.color}-600`}>{item.step}</span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">{item.phase}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Recent Patients */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Recent Patients</h2>
            <Link to="/patients" className="text-sm text-blue-600 hover:text-blue-800">
              View all
            </Link>
          </div>
          <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
            {recentPatients.length > 0 ? (
              recentPatients.map((patient) => (
                <Link
                  key={patient.id}
                  to={`/patients/${patient.id}`}
                  className="block px-6 py-4 hover:bg-gray-50 transition"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-700 font-medium">
                        {(patient.firstName || '').charAt(0)}{(patient.lastName || '').charAt(0)}
                      </span>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-xs text-gray-500">
                        Added {formatDate(patient.createdAt)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <p className="text-sm">No patients yet</p>
                <Link to="/patients/new" className="text-sm text-blue-600 hover:text-blue-800 mt-1 inline-block">
                  Add your first patient
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
            {hasPermission(userProfile?.role, 'viewAuditLogs') && (
              <Link to="/audit" className="text-sm text-blue-600 hover:text-blue-800">
                View all
              </Link>
            )}
          </div>
          <div className="bg-white rounded-lg shadow">
            {recentActivity.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {recentActivity.map((log) => (
                  <div key={log.id} className="px-6 py-3 flex items-center">
                    <div className={`flex-shrink-0 w-2 h-2 rounded-full mr-3 ${
                      log.action === 'create' ? 'bg-green-400' :
                      log.action === 'update' ? 'bg-yellow-400' :
                      log.action === 'delete' ? 'bg-red-400' :
                      log.action === 'login' ? 'bg-blue-400' :
                      'bg-gray-400'
                    }`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700">{getActivityDescription(log)}</p>
                    </div>
                    <div className="text-xs text-gray-400 ml-4">
                      {formatTime(log.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No recent activity</p>
                <p className="text-xs text-gray-400">Create your first patient to get started</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
